import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { getEnvironment } from "../constants/app-url"
import { ActionState, initialState } from "../state/core.state"

// ── Helper used by both setActiveMenu and the legacy extraReducer ────
function extractMenus(arr: any[]): any[] {
  let result: any[] = []
  arr.forEach((item) => {
    if (item.menus) {
      result = result.concat(item.menus)
      result = result.concat(extractMenus(item.menus))
    }
  })
  return result
}

function findMenuByUrl(applications: any[], url: string) {
  // Parent menus have an empty url; never let them match by accident. Prevents incorrect menu highlight
  if (!url) return null
  const applicationMenus = applications.reduce((a, b: any) => {
    if (b.menus) {
      const appMenus = b.menus
      const settingsMenu = b?.settings ?? []
      const flattenedAppMenus = extractMenus(appMenus)
      return a.concat([...flattenedAppMenus, ...appMenus, ...settingsMenu])
    } else {
      return a
    }
  }, [])
  return applicationMenus.find((a: any) => url === a.url) ?? null
}

function resolveActiveApp(applications: any[], appName: string | string[] | null) {
  let activeApp: any = null
  const mountedApps: any[] = []

  if (!appName) {
    return {
      activeApp: null,
      mountedApps: [],
      activeMenu: null,
      pageTitle: null,
      appTitle: null,
      routePrefix: null,
      appPrefix: null
    }
  }

  applications?.find((a) => {
    if (a.isSPA) {
      activeApp = { ...a }
      for (const mod of a.modules) {
        if (appName?.includes(mod.SPAName)) {
          mountedApps.push(mod)
        }
      }
      const moduleName = a.modules.find(
        (b) => appName?.includes(b.SPAName) && !b.location
      )
      if (moduleName !== undefined) {
        activeApp.modules = moduleName
      } else {
        activeApp = null
      }
      return moduleName
    }
  })

  const activeMenu = activeApp?.menus?.find(
    (a) => window.location.pathname === a.url
  )

  return {
    activeApp,
    mountedApps,
    activeMenu: activeMenu ?? null,
    pageTitle: activeMenu?.pageTitle ?? null,
    appTitle: activeApp?.applicationName ?? null,
    routePrefix: activeApp?.modules?.moduleURLPrefix ?? null,
    appPrefix:
      activeApp?.modules?.moduleUrls?.localPrefix ??
      activeApp?.modules?.moduleUrls?.prefix ??
      null
  }
}

function computeSettingsMenu(applications: any[]) {
  return applications.reduce((a, b) => {
    const settings = b?.settings?.length ? [...b.settings] : []
    return [...a, ...settings.filter((val) => val.url)]
  }, [])
}

// ── Payload types for compound actions ───────────────────────────────

interface IdTokenPayload {
  idToken: any
  idTokenEncoded: string
}

interface FoneAlertConfigPayload {
  status: boolean
  alertProps: Record<string, any> | null
}

// ── Slice ────────────────────────────────────────────────────────────

export const coreSlice = createSlice({
  name: "core",
  initialState,

  // ── NEW: Typed action creators ─────────────────────────────────────
  // Consumers dispatch via: coreStore.dispatch(coreActions.setLoaderStatus(true))
  // Each generates a standard { type: "core/...", payload: T } action.
  reducers: {
    setApplications(state, action: PayloadAction<any[]>) {
      state.applications = action.payload
      state.settingsMenu = computeSettingsMenu(action.payload)
      state.environment = getEnvironment()
    },

    // Accepts a single SPAName or an array (single-spa's `MOUNTED` list).
    // The reducer duck-types via `.includes`, so both shapes work.
    setActiveApp(state, action: PayloadAction<string | string[] | null>) {
      const result = resolveActiveApp(state.applications, action.payload)
      state.activeApp = result.activeApp
      state.mountedApps = result.mountedApps
      state.activeMenu = result.activeMenu ?? state.activeMenu
      state.pageTitle = result.pageTitle
      state.appTitle = result.appTitle
      state.routePrefix = result.routePrefix
      state.appPrefix = result.appPrefix
    },

    setActiveMenu(state, action: PayloadAction<string>) {
      const menu = findMenuByUrl(state.applications, action.payload)
      if (menu) {
        state.activeMenu = menu
      }
    },

    setUserToken(state, action: PayloadAction<string | null>) {
      state.userToken = action.payload
    },

    setIdToken(state, action: PayloadAction<IdTokenPayload>) {
      state.idToken = action.payload.idToken
      state.idTokenEncoded = action.payload.idTokenEncoded
    },

    setUserInfo(state, action: PayloadAction<Record<string, any> | null>) {
      state.userInfo = action.payload
    },

    setShowHomepage(state, action: PayloadAction<boolean>) {
      state.showHomepage = action.payload
    },

    setUseNewIframe(state, action: PayloadAction<boolean>) {
      state.useNewIframe = action.payload
    },

    setUseSSEEUURL(state, action: PayloadAction<boolean>) {
      state.useSSEEUURL = action.payload
    },

    resetOnLogout(state) {
      state.applications = []
      state.activeApp = null
      state.initialLoad = true
      state.userToken = null
      state.companyId = null
      state.userInfo = null
      state.interAppData = null
      state.showGlobalLoader = false
      state.showWhiteLoader = true
      state.appTitle = null
      state.pageTitle = null
      state.mountedApps = []
      state.activeMenu = null
      state.settingsMenu = []
      state.isSettingsPage = false
      state.applicationMessages = {}
      state.eulaConfig = null
      state.idToken = null
      state.idTokenEncoded = null
      state.routePrefix = null
      state.appPrefix = null
      // Previous user's AI alerts/surface context, provisioning banners,
      // visible alert modals and tenant feature flags must not leak into the
      // next session (surfaceContext kept from FPN-6378's aiPanel reset).
      state.aiPanel = {
        isOpen: false,
        isEnabled: false,
        alerts: [],
        surfaceContext: null
      }
      state.provisioningMessages = []
      state.foneAlertConfig = { status: false, alertProps: null }
      state.showHomepage = false
      state.useNewIframe = false
      state.useSSEEUURL = false
      state.localAuthRequired = false
    },

    setInterAppData(state, action: PayloadAction<Record<string, any> | null>) {
      state.interAppData = action.payload
    },

    setPageTitle(state, action: PayloadAction<string | null>) {
      state.pageTitle = action.payload
    },

    setAppTitle(state, action: PayloadAction<string | null>) {
      state.appTitle = action.payload
    },

    setLoaderStatus(state, action: PayloadAction<boolean>) {
      state.showGlobalLoader = action.payload
    },

    setSettingsType(state, action: PayloadAction<boolean>) {
      state.isSettingsPage = action.payload
    },

    setApplicationMessages(state, action: PayloadAction<Record<string, any>>) {
      state.applicationMessages = action.payload
    },

    setPendingEulas(state, action: PayloadAction<Record<string, any> | null>) {
      state.eulaConfig = action.payload
    },

    setFoneAlertConfig(state, action: PayloadAction<FoneAlertConfigPayload>) {
      state.foneAlertConfig = action.payload
    },

    setProvisioningMessages(state, action: PayloadAction<any[]>) {
      state.provisioningMessages = action.payload
    },

    toggleAIPanel(state) {
      state.aiPanel!.isOpen = !state.aiPanel!.isOpen
    },

    setAIPanelAlerts(state, action: PayloadAction<Record<string, any>[]>) {
      state.aiPanel!.alerts = action.payload
    },

    removeAIPanelAlert(state, action: PayloadAction<string>) {
      state.aiPanel!.alerts = state.aiPanel!.alerts.filter(
        (alert) => alert.id !== action.payload
      )
    },

    clearAIPanelAlerts(state) {
      state.aiPanel!.alerts = []
    },

    setIsAIEnabled(state, action: PayloadAction<boolean>) {
      state.aiPanel!.isEnabled = action.payload
    },

    // ── App-driven ARIA surfaces ──────────────────────────────────────
    // A page publishes its context so ARIA can render a matching custom
    // surface. The payload is opaque to coreui — the ARIA/consuming side owns
    // the shape.
    setAISurfaceContext(state, action: PayloadAction<Record<string, any> | null>) {
      state.aiPanel!.surfaceContext = action.payload
    },
    clearAISurfaceContext(state) {
      state.aiPanel!.surfaceContext = null
    },

    setLocalAuthRequired(state) {
      state.localAuthRequired = true
      state.showGlobalLoader = false
    },

    // Convenience actions for alerts (replaces showFoneAlert/closeFoneAlert utilities)
    showAlert(state, action: PayloadAction<Record<string, any>>) {
      state.foneAlertConfig = { status: true, alertProps: action.payload }
    },

    closeAlert(state) {
      state.foneAlertConfig = { status: false, alertProps: null }
    }
  },

  // ── LEGACY: Backward compatibility with existing consumer dispatches ─
  // These handle the old plain-object patterns like:
  //   coreStore.dispatch({ type: SET_ACTIVE_MENU, url: "/foo" })
  // Remove once all consumers are migrated to coreActions.
  extraReducers: (builder) => {
    builder
      .addCase(ActionState.SET_APPLICATIONS, (state, action: any) => {
        state.applications = action.applications
        state.settingsMenu = computeSettingsMenu(action.applications)
        state.environment = getEnvironment()
      })

      .addCase(ActionState.SET_ACTIVE_APP, (state, action: any) => {
        const result = resolveActiveApp(state.applications, action.appName)
        state.activeApp = result.activeApp
        state.mountedApps = result.mountedApps
        state.activeMenu = result.activeMenu ?? state.activeMenu
        state.pageTitle = result.pageTitle
        state.appTitle = result.appTitle
        state.routePrefix = result.routePrefix
        state.appPrefix = result.appPrefix
      })

      .addCase(ActionState.SET_ACTIVE_MENU, (state, action: any) => {
        const menu = findMenuByUrl(state.applications, action.url)
        if (menu) {
          state.activeMenu = menu
        }
      })

      .addCase(ActionState.SET_USER_TOKEN, (state, action: any) => {
        state.userToken = action.userToken
      })

      .addCase(ActionState.SET_ID_TOKEN, (state, action: any) => {
        state.idToken = action.idToken
        state.idTokenEncoded = action.idTokenEncoded
      })

      .addCase(ActionState.SET_USER_INFO, (state, action: any) => {
        state.userInfo = action.userResponse
      })

      .addCase(ActionState.SET_SHOW_HOMEPAGE, (state, action: any) => {
        state.showHomepage = action.showHomepage
      })

      .addCase(ActionState.SET_USE_NEW_IFRAME, (state, action: any) => {
        state.useNewIframe = action.useNewIframe
      })

      .addCase(ActionState.SET_USE_SSE_EU_URL, (state, action: any) => {
        state.useSSEEUURL = action.useSSEEUURL
      })

      .addCase(ActionState.SET_APP_LOGOUT_SUCCESS, (state) => {
        state.applications = []
        state.activeApp = null
        state.initialLoad = true
        state.userToken = null
        state.companyId = null
        state.userInfo = null
        state.interAppData = null
        state.showGlobalLoader = false
        state.showWhiteLoader = true
        state.appTitle = null
        state.pageTitle = null
        state.mountedApps = []
        state.activeMenu = null
        state.settingsMenu = []
        state.isSettingsPage = false
        state.applicationMessages = {}
        state.eulaConfig = null
        state.idToken = null
        state.idTokenEncoded = null
        state.routePrefix = null
        state.appPrefix = null
        // Keep in sync with resetOnLogout above (surfaceContext from FPN-6378)
        state.aiPanel = {
          isOpen: false,
          isEnabled: false,
          alerts: [],
          surfaceContext: null
        }
        state.provisioningMessages = []
        state.foneAlertConfig = { status: false, alertProps: null }
        state.showHomepage = false
        state.useNewIframe = false
        state.useSSEEUURL = false
        state.localAuthRequired = false
      })

      .addCase(ActionState.SET_INTER_APP_DATA, (state, action: any) => {
        state.interAppData = action.interAppData
      })

      .addCase(ActionState.SET_PAGE_TITLE, (state, action: any) => {
        state.pageTitle = action.pageTitle
      })

      .addCase(ActionState.SET_APP_TITLE, (state, action: any) => {
        state.appTitle = action.appTitle
      })

      .addCase(ActionState.SET_LOADER_STATUS, (state, action: any) => {
        state.showGlobalLoader = action.status
      })

      .addCase(ActionState.SET_SETTINGS_TYPE, (state, action: any) => {
        state.isSettingsPage = action.status
      })

      .addCase(
        ActionState.SET_APPLICATION_MESSAGES_SUCCESS,
        (state, action: any) => {
          state.applicationMessages = action.messages
        }
      )

      .addCase(ActionState.SET_PENDING_EULAS, (state, action: any) => {
        state.eulaConfig = action.payload
      })

      .addCase(ActionState.SET_FONE_ALERT_CONFIG, (state, action: any) => {
        state.foneAlertConfig = action.payload
      })

      .addCase(
        ActionState.SET_PROVISIONING_MESSAGES,
        (state, action: any) => {
          state.provisioningMessages = action.payload
        }
      )

      .addCase(ActionState.TOGGLE_AI_PANEL, (state) => {
        state.aiPanel!.isOpen = !state.aiPanel!.isOpen
      })

      .addCase(ActionState.SET_AI_PANEL_ALERTS, (state, action: any) => {
        state.aiPanel!.alerts = action.payload
      })

      .addCase(ActionState.REMOVE_AI_PANEL_ALERT, (state, action: any) => {
        state.aiPanel!.alerts = state.aiPanel!.alerts.filter(
          (alert) => alert.id !== action.payload
        )
      })

      .addCase(ActionState.CLEAR_AI_PANEL_ALERTS, (state) => {
        state.aiPanel!.alerts = []
      })

      .addCase(ActionState.SET_IS_AI_ENABLED, (state, action: any) => {
        state.aiPanel!.isEnabled = action.payload
      })

      .addCase(ActionState.LOCAL_AUTH_REQUIRED, (state) => {
        state.localAuthRequired = true
        state.showGlobalLoader = false
      })
  }
})

/**
 * Typed action creators — the recommended way to dispatch state changes.
 *
 * Usage:
 *   import { coreActions } from "@forcepoint/platform-coreui"
 *   coreStore.dispatch(coreActions.setLoaderStatus(true))
 */
export const coreActions = coreSlice.actions

export default coreSlice.reducer
